'use client';

import { useCallback, useReducer, useRef } from 'react';

import {
  DEFAULT_MAX_RETRIES,
  DEFAULT_POLLING_INTERVAL,
  FETCH_ERROR,
  FETCH_RETRY,
  FETCH_START,
  FETCH_SUCCESS,
  RESET,
  START_POLLING,
  STOP_POLLING,
} from './constants';
import type {
  EndpointPollingOptions,
  PollingAction,
  PollingState,
  UseEndpointPollingResult,
} from './types';

const createInitialState = <TData>(): PollingState<TData> => ({
  data: null,
  isLoading: false,
  isPolling: false,
  error: null,
  retryCount: 0,
});

const pollingReducer = <TData>(
  state: PollingState<TData>,
  action: PollingAction<TData>,
): PollingState<TData> => {
  switch (action.type) {
    case FETCH_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case FETCH_SUCCESS:
      return {
        ...state,
        data: action.payload,
        isLoading: false,
        error: null,
      };

    case FETCH_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };

    case FETCH_RETRY:
      return {
        ...state,
        retryCount: state.retryCount + 1,
        isLoading: true,
      };

    case STOP_POLLING:
      return {
        ...state,
        isPolling: false,
      };

    case START_POLLING:
      return {
        ...state,
        isPolling: true,
        error: null,
      };

    case RESET:
      return createInitialState<TData>();

    default:
      return state;
  }
};

/**
 * A hook to handle polling an endpoint with configurable retry behavior.
 * Polling stops when data is successfully returned, unless shouldContinuePolling is provided.
 * Retries will occur on errors up to the configured maximum.
 *
 * @param fetchFunction - The function to fetch data
 * @param options - Polling configuration options
 * @returns Polling state and control functions
 */
const useEndpointPolling = <TData>(
  fetchFunction: () => Promise<TData>,
  options: EndpointPollingOptions<TData> = {},
): UseEndpointPollingResult<TData> => {
  const {
    interval = DEFAULT_POLLING_INTERVAL,
    retries = DEFAULT_MAX_RETRIES,
    shouldContinuePolling,
  } = options;
  const [state, dispatch] = useReducer(pollingReducer<TData>, createInitialState<TData>());
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Use a ref to always track the current state
  // This prevents stale state from being used within fetchData
  const stateRef = useRef<PollingState<TData>>(state);
  // Update the ref whenever state changes
  stateRef.current = state;

  const fetchData = async () => {
    try {
      dispatch({ type: FETCH_START });
      const data = await fetchFunction();
      options.onResponse?.(data);
      dispatch({ type: FETCH_SUCCESS, payload: data });
      const currentState = stateRef.current;

      if (currentState.isPolling) {
        const shouldContinue = shouldContinuePolling ? shouldContinuePolling(data) : false;
        if (shouldContinue && currentState.retryCount < retries - 1) {
          // Schedule next poll
          timeoutRef.current = setTimeout(() => {
            dispatch({ type: FETCH_RETRY });
            void fetchData();
          }, interval);
        } else {
          // Stop polling if we don't need to continue
          if (currentState.retryCount >= retries - 1) {
            options.onTimedOut?.();
          } else {
            options.onCancel?.(data);
          }
          dispatch({ type: STOP_POLLING });
        }
      }
    } catch (error) {
      dispatch({
        type: FETCH_ERROR,
        payload: error instanceof Error ? error : new Error('An unknown error occurred'),
      });
      dispatch({ type: STOP_POLLING });
    }
  };

  const startPolling = useCallback(() => {
    dispatch({ type: START_POLLING });
    void fetchData();
    // We don't want this function to re-initialize on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = useCallback(() => {
    dispatch({ type: STOP_POLLING });
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    // We don't want this function to re-initialize on every render
  }, []);

  const reset = useCallback(() => {
    stopPolling();
    dispatch({ type: RESET });
    // We don't want this function to re-initialize on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...state,
    startPolling,
    stopPolling,
    reset,
  };
};

export default useEndpointPolling;
